import { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Text } from '@belong/ui';
import clsx from 'clsx';
import ModalV2 from 'components/Modal/ModalV2/ModalV2';
import CreateAchJpm from 'containercomponents/PaymentMethods/CreateAchJpm/CreateAchJpm';
import { PAYMENT_SCREENS } from 'containercomponents/Modals/PaymentsModal/PaymentsModal';
import { Col, Row } from 'forkedlibraries/react-bootstrap';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import PropTypes from 'prop-types';
import {
  fetchAchBankAccountSetups,
  fetchPaymentMethods,
  fetchUserPaymentMethodConfigurations,
  fetchUserAutoSplitPaymentMethod,
  updatePaymentMethod,
  updateUserAutoSplitPaymentMethod,
} from 'store/redux/user/actions';
import {
  selectFullUser,
  selectUserAutoSplit,
  selectUserPaymentMethodConfigurations,
} from 'store/redux/user/selectors';
import {
  PaymentMethod,
  PaymentPreference,
  PaymentPriority,
} from '../../models/enums';
import { getString } from '../../strings';
import { FEEDBACK_STRINGS } from '../../strings/errors.string';
import PaymentTile from '../../components/PaymentTile/PaymentTile';
import PaymentTileContainer from '../PaymentTileContainer/PaymentTileContainer';
import PaymentMethodModel from '../../models/common/PaymentMethod';

const VERIFY_ACTION_STATUSES = [
  'ActionRequired',
  'PendingMicrodeposit',
  'PendingUserAction',
];
const VERIFIED_STATUS = 'Verified';

function hasPrimaryPreference(paymentMethod, paymentType) {
  return paymentMethod?.preferences?.some(
    (preference) =>
      preference.paymentType === paymentType &&
      preference.priority === PaymentPriority.Primary,
  );
}

function getVerifiedAchWithoutPreferences(fullUser) {
  return fullUser?.paymentMethods?.paymentMethods?.find(
    (paymentMethod) =>
      paymentMethod.paymentMethod === PaymentMethod.Ach &&
      paymentMethod.isVerified &&
      !paymentMethod.preferences?.length,
  );
}

function getVerifiedCreatedSetup(setups) {
  return setups
    .filter(
      (setup) =>
        setup.status === VERIFIED_STATUS &&
        setup.isPaymentMethodCreated &&
        setup.paymentAccountUniqueId,
    )
    .sort((left, right) => {
      const leftStartedOn = new Date(left.startedOn).getTime();
      const rightStartedOn = new Date(right.startedOn).getTime();
      return rightStartedOn - leftStartedOn;
    })[0];
}

function getVerifiedSetupPaymentAccountId(setups, sessionId) {
  return setups.find(
    (setup) =>
      setup.sessionId === sessionId &&
      setup.status === VERIFIED_STATUS &&
      setup.isPaymentMethodCreated &&
      setup.paymentAccountUniqueId,
  )?.paymentAccountUniqueId;
}

function normalize(value) {
  return value?.trim?.().toLowerCase() ?? '';
}

function isMatchingSetup(setup, paymentMethod) {
  const lastDigitsMatch =
    normalize(setup?.lastDigits) === normalize(paymentMethod?.lastDigits);
  const bankNameMatch =
    !setup?.bankName ||
    !paymentMethod?.achDetails?.bankName ||
    normalize(setup.bankName) === normalize(paymentMethod.achDetails.bankName);
  const accountTypeMatch =
    !setup?.accountType ||
    !paymentMethod?.achDetails?.accountType ||
    normalize(setup.accountType) ===
      normalize(paymentMethod.achDetails.accountType);

  return (
    VERIFY_ACTION_STATUSES.includes(setup?.status) &&
    lastDigitsMatch &&
    bankNameMatch &&
    accountTypeMatch
  );
}

function toPendingPaymentMethod(setup) {
  const paymentMethod = new PaymentMethodModel({
    accountId: setup.sessionId,
    paymentMethod: PaymentMethod.Ach,
    isVerified: false,
    lastDigits: setup.lastDigits || '',
    achDetails: {
      accountType: setup.accountType || '',
      bankName: setup.bankName || '',
    },
  });

  paymentMethod.jpmAchSetup = true;

  return paymentMethod;
}

function PaymentMethodContainer(props) {
  const {
    isSelector,
    primaryOnly,
    isRenter = false,
    isReferral = false,
    fullUser,
    maintenanceOnly,
    hasLabels,
    updateUserAutoSplitPaymentMethod: updateAutoSplit,
    shouldShowAutoSplitPaymentMethod,
    isAutoSplitEnabled,
    defaultPriority,
    variant,
    fetchAchBankAccountSetups: fetchAchBankAccountSetupsAction,
    fetchPaymentMethods: fetchPaymentMethodsAction,
    fetchUserPaymentMethodConfigurations:
      fetchUserPaymentMethodConfigurationsAction,
    fetchUserAutoSplitPaymentMethod: fetchUserAutoSplitAction,
    updatePaymentMethod: updatePaymentMethodAction,
  } = props;

  const [autoSplitError, setAutoSplitError] = useState();
  const [verificationError, setVerificationError] = useState();
  const [verifyingSetupId, setVerifyingSetupId] = useState();
  const [achBankAccountSetups, setAchBankAccountSetups] = useState([]);
  const repairedJpmPreferenceRef = useRef(null);
  const { isEnabled: isJpmAchEnabled } = useFeatureFlag(
    'ach-jpm-new-work-enabled',
  );

  const refreshAchBankAccountSetups = useCallback(async () => {
    if (!isJpmAchEnabled) {
      return [];
    }

    try {
      const setups = await fetchAchBankAccountSetupsAction();
      setAchBankAccountSetups(setups || []);
      return setups || [];
    } catch (err) {
      console.error(err);
      return [];
    }
  }, [fetchAchBankAccountSetupsAction, isJpmAchEnabled]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchPaymentMethodsAction(),
          fetchUserAutoSplitAction(),
          fetchUserPaymentMethodConfigurationsAction(),
        ]);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [
    fetchPaymentMethodsAction,
    fetchUserAutoSplitAction,
    fetchUserPaymentMethodConfigurationsAction,
  ]);

  useEffect(() => {
    async function loadAchBankAccountSetups() {
      await refreshAchBankAccountSetups();
    }

    loadAchBankAccountSetups();
  }, [refreshAchBankAccountSetups]);

  const handlePaymentSelection = async (priority, accountId) => {
    try {
      await updatePaymentMethodAction(accountId, priority, defaultPriority);
    } catch (err) {
      console.error(err);
    }
  };

  const onAutoSplitError = (error) => {
    if (error[0].message.includes('Yearly Payout Plan')) {
      setAutoSplitError(
        'Split It payment method can not be enabled because you are in a Yearly Payout Plan.',
      );
      setTimeout(() => {
        setAutoSplitError(undefined);
      }, 6000);
    }
  };

  const handleVerifyPaymentMethod = async (paymentMethod) => {
    setVerificationError(undefined);

    let matchingSetup = achBankAccountSetups.find((setup) =>
      isMatchingSetup(setup, paymentMethod),
    );

    if (!matchingSetup) {
      const refreshedSetups = await refreshAchBankAccountSetups();
      matchingSetup = refreshedSetups.find((setup) =>
        isMatchingSetup(setup, paymentMethod),
      );
    }

    if (!matchingSetup) {
      await fetchPaymentMethodsAction();
      setVerificationError(
        'Sorry! We were not able to verify your bank account. Please try again.',
      );
      return;
    }

    setVerifyingSetupId(matchingSetup.sessionId);
  };

  const handleCloseJpmVerification = async (
    status,
    shouldSelectAsPrimary = false,
  ) => {
    const verifiedSessionId = status?.sessionId || verifyingSetupId;
    setVerifyingSetupId(undefined);
    const refreshedSetups = await refreshAchBankAccountSetups();
    const verifiedPaymentAccountId =
      status?.accountId ||
      status?.paymentAccountUniqueId ||
      getVerifiedSetupPaymentAccountId(refreshedSetups, verifiedSessionId);

    if (shouldSelectAsPrimary && verifiedPaymentAccountId) {
      try {
        await updatePaymentMethodAction(
          verifiedPaymentAccountId,
          primaryPaymentType,
          PaymentPriority.Primary,
        );
        return;
      } catch (err) {
        console.error(err);
      }
    }

    await fetchPaymentMethodsAction();
  };

  const handleAchVerificationStatus = async () => {
    setVerificationError(undefined);

    await refreshAchBankAccountSetups();
    await fetchPaymentMethodsAction();
  };

  function getForcePaymentMethod(paymentType) {
    const { paymentMethodConfigurations } = props;

    const allowedTypes =
      paymentMethodConfigurations?.[paymentType]?.allowedPaymentMethods || {};
    const allowedTypesArray = Object.keys(allowedTypes).filter(
      (allowedType) => allowedTypes[allowedType],
    );

    if (allowedTypesArray.length === 1) {
      return allowedTypesArray[0];
    }

    return null;
  }

  const primaryPaymentType = isRenter
    ? PaymentPreference.Rent
    : PaymentPreference.Payout;
  const primaryPaymentMethod = fullUser.getPaymentMethodFor(
    primaryPaymentType,
    PaymentPriority.Primary,
  );
  const primaryAchPaymentMethod = fullUser.getPaymentMethodFor(
    primaryPaymentType,
    PaymentPriority.Primary,
    PaymentMethod.Ach,
  );
  const verifiedAchWithoutPreferences =
    getVerifiedAchWithoutPreferences(fullUser);
  const verifiedCreatedSetup = getVerifiedCreatedSetup(achBankAccountSetups);
  const jpmPreferenceRepairAccountId =
    verifiedAchWithoutPreferences?.accountId ||
    verifiedCreatedSetup?.paymentAccountUniqueId;

  useEffect(() => {
    if (
      !isJpmAchEnabled ||
      primaryPaymentMethod ||
      !jpmPreferenceRepairAccountId
    ) {
      return;
    }

    if (repairedJpmPreferenceRef.current === jpmPreferenceRepairAccountId) {
      return;
    }

    repairedJpmPreferenceRef.current = jpmPreferenceRepairAccountId;
    void (async () => {
      try {
        await updatePaymentMethodAction(
          jpmPreferenceRepairAccountId,
          primaryPaymentType,
          PaymentPriority.Primary,
        );
      } catch (err) {
        console.error(err);
      }
    })();
  }, [
    isJpmAchEnabled,
    jpmPreferenceRepairAccountId,
    primaryPaymentMethod,
    primaryPaymentType,
    updatePaymentMethodAction,
  ]);

  const renderPaymentTile = (
    preference,
    showPaymentTileMarginOnMobile,
    showAutoSplit = false,
    priority = PaymentPriority.Primary,
    extraMenuProps = {},
  ) => {
    const { showPriority, noCustomWidth } = props;
    const paymentType = preference;

    if (
      isJpmAchEnabled &&
      priority === PaymentPriority.Primary &&
      (paymentType === PaymentPreference.Rent ||
        paymentType === PaymentPreference.Payout) &&
      !primaryPaymentMethod &&
      !primaryAchPaymentMethod &&
      verifiedAchWithoutPreferences &&
      !hasPrimaryPreference(verifiedAchWithoutPreferences, paymentType)
    ) {
      return (
        <PaymentTile
          fullUser={fullUser}
          isRenter={isRenter}
          isReferral={isReferral}
          showPriority={showPriority}
          showPaymentTileMarginOnMobile={showPaymentTileMarginOnMobile}
          paymentMethod={verifiedAchWithoutPreferences}
          highlight={false}
          showSpinner={false}
          noCustomWidth={noCustomWidth}
          priority={priority}
          isSelector={isSelector}
          paymentType={paymentType}
          paymentModalProps={{}}
          hasLabels={hasLabels}
          menuProps={{
            forcePaymentMethod: PaymentMethod.Ach,
            onMenuOpen: () => {},
            onMenuClose: () => {},
            onSelectPaymentMethod: (accountId) =>
              handlePaymentSelection(paymentType, accountId),
            selectedPaymentMethodId: verifiedAchWithoutPreferences.accountId,
            ignorePaymentMethodId: null,
            onSelectAutoSplitPaymentMethod: () => {},
            shouldShowAutoSplitPaymentMethod: false,
            isAutoSplitEnabled: false,
            isJpmAchEnabled,
            onVerifyPaymentMethod: handleVerifyPaymentMethod,
            ...extraMenuProps,
          }}
        />
      );
    }

    return (
      <PaymentTileContainer
        isRenter={isRenter}
        isReferral={isReferral}
        showPriority={showPriority}
        isSelector={isSelector}
        showPaymentTileMarginOnMobile={showPaymentTileMarginOnMobile}
        paymentType={paymentType}
        priority={priority}
        noCustomWidth={noCustomWidth}
        forcePaymentMethod={getForcePaymentMethod(paymentType)}
        onPaymentSelection={handlePaymentSelection}
        onAutoSplitSelection={updateAutoSplit}
        onAutoSplitError={onAutoSplitError}
        shouldShowAutoSplitPaymentMethod={
          shouldShowAutoSplitPaymentMethod &&
          preference === PaymentPreference.Maintenance
        }
        isAutoSplitEnabled={isAutoSplitEnabled && showAutoSplit}
        ignorePaymentMethodId={null}
        hasLabels={hasLabels}
        isJpmAchEnabled={isJpmAchEnabled}
        onVerifyPaymentMethod={handleVerifyPaymentMethod}
        {...extraMenuProps}
        paymentModalProps={{
          screen: PAYMENT_SCREENS.PAYMENT_SELECTION,
          forcePaymentMethod: getForcePaymentMethod(paymentType),
          ctaLabel: `Set ${preference}`,
          errorScreenProps: {
            title: FEEDBACK_STRINGS.set_payment_method.error.title,
            subtitle: getString(
              FEEDBACK_STRINGS.set_payment_method.error.subtitle,
              {
                priority: preference ? preference.toLowerCase() : '',
              },
            ),
            ctaLabel: FEEDBACK_STRINGS.set_payment_method.error.ctaLabel,
          },
          successScreenProps: {
            title: FEEDBACK_STRINGS.set_payment_method.success.title,
            subtitle: getString(
              FEEDBACK_STRINGS.set_payment_method.success.subtitle,
              {
                priority: preference ? preference.toLowerCase() : '',
              },
            ),
            ctaLabel: FEEDBACK_STRINGS.set_payment_method.success.ctaLabel,
          },
          onAchVerificationStatus: handleAchVerificationStatus,
        }}
        variant={variant}
      />
    );
  };

  const pendingAchSetups = isJpmAchEnabled
    ? achBankAccountSetups.filter(
        (setup) =>
          !setup.isPaymentMethodCreated &&
          VERIFY_ACTION_STATUSES.includes(setup.status),
      )
    : [];
  const pendingPrimaryAchSetup = pendingAchSetups[0];
  const pendingPrimaryAchPaymentMethod = pendingPrimaryAchSetup
    ? toPendingPaymentMethod(pendingPrimaryAchSetup)
    : null;

  const pendingPrimaryAchMenuProps =
    (primaryPaymentMethod || verifiedAchWithoutPreferences) &&
    pendingPrimaryAchPaymentMethod
      ? {
          extraPaymentMethods: [pendingPrimaryAchPaymentMethod],
          onVerifyPaymentMethod: () =>
            setVerifyingSetupId(pendingPrimaryAchSetup.sessionId),
        }
      : {};

  const renderPendingAchSetupTile = (setup) => {
    const paymentMethod = toPendingPaymentMethod(setup);
    const paymentType = isRenter
      ? PaymentPreference.Rent
      : PaymentPreference.Payout;

    return (
      <PaymentTile
        key={setup.sessionId}
        fullUser={fullUser}
        isRenter={isRenter}
        isReferral={isReferral}
        showPriority={true}
        showPaymentTileMarginOnMobile={true}
        paymentMethod={paymentMethod}
        highlight={false}
        showSpinner={false}
        noCustomWidth={false}
        priority={PaymentPriority.Primary}
        isSelector={isSelector}
        paymentType={paymentType}
        paymentModalProps={{}}
        hasLabels={hasLabels}
        menuProps={{
          forcePaymentMethod: PaymentMethod.Ach,
          onMenuOpen: () => {},
          onMenuClose: () => {},
          onSelectPaymentMethod: () => {},
          selectedPaymentMethodId: paymentMethod.accountId,
          ignorePaymentMethodId: null,
          onSelectAutoSplitPaymentMethod: () => {},
          shouldShowAutoSplitPaymentMethod: false,
          isAutoSplitEnabled: false,
          isJpmAchEnabled,
          isPendingJpmSetup: true,
          onVerifyPaymentMethod: () => setVerifyingSetupId(setup.sessionId),
        }}
      />
    );
  };

  const renderPrimaryPaymentTile = () => {
    if (primaryPaymentMethod || verifiedAchWithoutPreferences) {
      return renderPaymentTile(
        primaryPaymentType,
        true,
        false,
        PaymentPriority.Primary,
        pendingPrimaryAchMenuProps,
      );
    }

    if (pendingPrimaryAchSetup) {
      return renderPendingAchSetupTile(pendingPrimaryAchSetup);
    }

    return renderPaymentTile(primaryPaymentType, true);
  };

  const verificationFeedback = (
    <>
      {autoSplitError && <Text className="text-red">{autoSplitError}</Text>}
      {verificationError && (
        <Text className="text-red">{verificationError}</Text>
      )}
    </>
  );

  if (isSelector || primaryOnly) {
    return (
      <div className="mt-sm">
        <Row>
          <Col xs={12} lg={12}>
            {renderPrimaryPaymentTile()}
          </Col>
        </Row>
        {verificationFeedback}
      </div>
    );
  }

  if (isReferral) {
    return (
      <div className="mt-sm">
        <Row>
          <Col xs={12} lg={12}>
            {renderPaymentTile(PaymentPreference.Payout, true)}
          </Col>
        </Row>
        {verificationFeedback}
      </div>
    );
  }

  if (maintenanceOnly) {
    return (
      <div className="mt-sm">
        <Row>
          <Col xs={12} lg={12}>
            {renderPaymentTile(
              PaymentPreference.Maintenance,
              true,
              false,
              defaultPriority,
            )}
          </Col>
        </Row>
        {verificationFeedback}
      </div>
    );
  }

  return (
    <>
      {verifyingSetupId && (
        <ModalV2
          show
          onHide={() => handleCloseJpmVerification()}
          noBorder
          noPadding
          closeButton={false}
          backButton={false}
        >
          <CreateAchJpm
            sessionId={verifyingSetupId}
            onSuccess={(status) => handleCloseJpmVerification(status, true)}
            onClose={handleCloseJpmVerification}
            onFail={async () => {
              setVerificationError(
                'Sorry! We were not able to verify your bank account. Please try again.',
              );
              await handleCloseJpmVerification();
            }}
          />
        </ModalV2>
      )}
      <div
        className={clsx(
          'mt-sm grid grid-cols-1 gap-sm lg:grid-cols-2',
          autoSplitError ? 'mb-sm' : 'mb-2xl',
        )}
      >
        {renderPrimaryPaymentTile()}
        {renderPaymentTile(
          PaymentPriority.Maintenance,
          false,
          isAutoSplitEnabled,
        )}
        {!isRenter &&
          !isReferral &&
          renderPaymentTile(
            PaymentPreference.RentalHomeInsurance,
            false,
            false,
          )}
      </div>
      {verificationFeedback}
    </>
  );
}

PaymentMethodContainer.propTypes = {
  fetchAchBankAccountSetups: PropTypes.func.isRequired,
  fetchPaymentMethods: PropTypes.func.isRequired,
  updatePaymentMethod: PropTypes.func.isRequired,
  fetchUserAutoSplitPaymentMethod: PropTypes.func.isRequired,
  fetchUserPaymentMethodConfigurations: PropTypes.func.isRequired,
  updateUserAutoSplitPaymentMethod: PropTypes.func.isRequired,
  fullUser: PropTypes.object.isRequired,
  isSelector: PropTypes.bool,
  primaryOnly: PropTypes.bool,
  maintenanceOnly: PropTypes.bool,
  showPriority: PropTypes.bool,
  noCustomWidth: PropTypes.bool,
  isRenter: PropTypes.bool,
  hasLabels: PropTypes.bool,
  isAutoSplitEnabled: PropTypes.bool,
  shouldShowAutoSplitPaymentMethod: PropTypes.bool,
  isReferral: PropTypes.bool,
  paymentMethodConfigurations: PropTypes.object,
  defaultPriority: PropTypes.string,
  variant: PropTypes.string,
};

PaymentMethodContainer.defaultProps = {
  isSelector: false,
  primaryOnly: false,
  maintenanceOnly: false,
  showPriority: true,
  noCustomWidth: false,
  hasLabels: true,
  shouldShowAutoSplitPaymentMethod: false,
  isReferral: false,
  defaultPriority: PaymentPriority.Primary,
  variant: 'primary',
};

export default connect(
  (state) => ({
    fullUser: selectFullUser(state),
    isAutoSplitEnabled: selectUserAutoSplit(state),
    paymentMethodConfigurations: selectUserPaymentMethodConfigurations(state),
  }),
  {
    fetchPaymentMethods,
    fetchAchBankAccountSetups,
    fetchUserPaymentMethodConfigurations,
    updatePaymentMethod,
    fetchUserAutoSplitPaymentMethod,
    updateUserAutoSplitPaymentMethod,
  },
)(PaymentMethodContainer);
